import React, { useState, useRef, useEffect, useContext } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { z } from "zod";
import { UserContext } from "../UserContext";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { putClient } from "../API/clientAPI";
import { getJobOrders, putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { getTechnicians } from "../API/technicianAPI";
import SignatureCanvas from "react-signature-canvas";
import { patchJobOrderLink } from "../API/jobOrderAPI";
import { getJobOrder } from "../API/jobOrderAPI";
import { sendEmailQueue } from "../API/emailAPI";

import TermiteLocationCard from "./FormComponents/TermiteLocationCard";
import ExterraCard from "./FormComponents/ExterraCard";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from '@mui/icons-material/Circle';

// Import S3 configurations and bucket names
import {
  s3ReportsPDFUploader,
  REPORTS_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-reports";
import {
  s3InvoicePDFUploader,
  INVOICE_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-receipts";
import {
  s3TempBucketUploader,
  TEMP_BUCKET_NAME,
} from "../Promotions/config/aws-config-temp-bucket";

// Import PDF generation functions
import generateReceipts from "./GenerateReceipts";
import generateReports from "./GenerateReports";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function TermiteControlForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    inspection_report: [],
    chemical_used: [],
    equipment_used: [],
    service_fee: "",
    service_report_payment_status: [],
    service_report_payment_method: [],
    job_date: "",
    // end_date: "",
    start_time: "",
    // end_time: "",
    accomplished_by_technician: "",
    reminder_notes: "",
    satisfaction_rating: 0,
  });

  const [client, setClient] = useState({});

  const [editedClient, setEditedClient] = useState();

  const [links, setLinks] = useState({});

  // all techs for the dialog
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [editTechsOpen, setEditTechsOpen] = useState(false);
  const [editSelectedTechIds, setEditSelectedTechIds] = useState([]);

  const [technicianSignatory, setTechnicianSignatory] = useState("");

  const [satisfactionRating, setSatisfactionRating] = useState(0);

  const RECOMMENDATIONS = [
    "Schedule termite control",
    "Monitor regularly",
    "Seal entry points with caulking or foam",
    "Improve airflow, reduce moisture",
    "Repair leaks, dry affected zones",
    "Stagnant water / leaking pipes",
    "Relocate or elevate storage",
    "Closed room or area Inaccessible",
    "Ensure room access for full inspection",
  ];

  // --- Inspection Report (locations) schema helpers ---
  const isBlank = (v) => v == null || String(v).trim() === "";
  
  // A row is "empty" if user didn't touch anything
  const rowIsCompletelyEmpty = (row) =>
    isBlank(row.location) &&
    isBlank(row.location_other) &&
    isBlank(row.findings) &&
    isBlank(row.action_taken) &&
    isBlank(row.bait_monitor_location) &&
    isBlank(row.remarks);
  
  const LocationRowSchema = z.object({
    location: z.string().optional().transform(v => v ?? ""),
    location_other: z.string().optional().transform(v => v ?? ""),
    findings: z.string().optional().transform(v => v ?? ""),
    action_taken: z.string().optional().transform(v => v ?? ""),
    bait_monitor_location: z.string().optional().transform(v => v ?? ""),
    remarks: z.string().optional().transform(v => v ?? ""),
  }).superRefine((row, ctx) => {
    // allow fully empty rows (so users can leave extra cards blank)
    if (rowIsCompletelyEmpty(row)) return;
  
    // required core fields
    if (isBlank(row.location)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["location"], message: "Location is required" });
    }
    if (isBlank(row.findings)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["findings"], message: "Findings is required" });
    }
    if (isBlank(row.action_taken)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["action_taken"], message: "Action Taken is required" });
    }
    if (isBlank(row.remarks)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["remarks"], message: "Remarks is required" });
    }
    // conditional requirements
    if (row.location === "Other" && isBlank(row.location_other)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["location_other"], message: "Please specify other location" });
    }
    if (row.action_taken === "baiting" && isBlank(row.bait_monitor_location)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["bait_monitor_location"], message: "Please specify bait/monitor location" });
    }
  });

  const TermiteSchema = z.object({
    satisfaction_rating: z.coerce
      .number({
        required_error: "Please pick a rating (1–5).",
        invalid_type_error: "Please pick a rating (1–5).",
      })
      .min(1, { message: "Please pick a rating (1–5)." })
      .max(5, { message: "Please pick a rating (1–5)." }),
    acknowledgedSignature: z.string().min(1, "Please click Upload for Acknowledged signature."),
    accomplishedSignature: z.string().min(1, "Please click Upload for Accomplished signature."),
    acknowledgedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Acknowledged signature."),
    accomplishedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Accomplished signature."),
    technician_signatory: z.string().trim().min(1, "Please choose a technician signatory."),
    acknowledgedClientSignatory: z.string().trim().min(1, "Please enter the client representative."),
    status: z
      .string()
      .refine((v) => v && v !== "Not Done", {
        message: 'Please set Job Order Status to something other than "Not Done".',
    }),
    // Inspection Report rows
    locations: z.array(LocationRowSchema)
      .min(1, "Add at least one location")
      .superRefine((rows, ctx) => {
        // At least one FULL row must be present (not empty + passes requireds)
        const fullRows = rows.filter(r =>
          !rowIsCompletelyEmpty(r) &&
          !isBlank(r.location) &&
          !isBlank(r.findings) &&
          !isBlank(r.action_taken) &&
          !isBlank(r.remarks)
        );
        if (fullRows.length === 0) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [0, "location"], message: "Please complete at least one Inspection Report row." });
        }
      }),
    termite_recommendations: z
      .array(z.string())
      .min(1, "Please select at least one recommendation."),
  }).passthrough();


  const {
    control,
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(TermiteSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      // required fields
      satisfaction_rating: 0,
      acknowledgedSignature: "",
      accomplishedSignature: "",
      acknowledgedUploaded: false,
      accomplishedUploaded: false,
      technician_signatory: "",
      acknowledgedClientSignatory: "",
      status: "",
      // everything else that belongs to this form
      locations: [
        {
          location: "",
          location_other: "",
          findings: "",
          action_taken: "",
          bait_monitor_location: "",
          remarks: "",
          termite_activity: "",
          termite_recommendations: [],
          exterra_bait_stations: [],
        },
      ],
      termite_recommendations: [],
      exterra_bait_stations: [],
    },
  });

  // End time helper
  const getNowHHMM = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const saveAndUploadAccomplishedSignature = async () => {
    try {
      setIsLoading(true);

      // ⏰ CAPTURE TECHNICIAN SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setTechnicianSignatureUploadedAt(uploadTimestamp);

      const url = await resizeAndUploadSignature(
        accomplishedBySignatureRef,
        jobOrder.id,
        "accomplished"
      );
      setAccomplishedSignatureURL(url); // keep your state
      // ✅ write into the form and mark as uploaded
      setValue("accomplishedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", true, { shouldValidate: true });

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        technician_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      console.error("Error saving signature:", error);
      setErrorMessage(`Error saving signature: ${error.message}`);
      // ensure invalid if upload failed
      setValue("accomplishedSignature", "", { shouldValidate: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAccomplishedSignature = () => {
    accomplishedBySignatureRef.current?.clear();
    setAccomplishedSignatureURL("");
    setValue("accomplishedSignature", "", { shouldValidate: true, shouldDirty: true });
    setValue("accomplishedUploaded", false, { shouldValidate: true });
  };

  const saveAndUploadAcknowledgedSignature = async () => {
    try {
      setIsLoading(true);
      const url = await resizeAndUploadSignature(
        acknowledgedBySignatureRef,
        jobOrder.id,
        "acknowledged"
      );

      // ⏰ CAPTURE CLIENT SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setClientSignatureUploadedAt(uploadTimestamp);

      setAcknowledgedSignatureURL(url);
      // ✅ write into the form and mark as uploaded
      setValue("acknowledgedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", true, { shouldValidate: true });

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        client_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      console.error("Error saving signature:", error);
      setErrorMessage("Error saving signature. Please try again.");
      setValue("acknowledgedSignature", "", { shouldValidate: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAcknowledgedSignature = () => {
    acknowledgedBySignatureRef.current?.clear();
    setAcknowledgedSignatureURL("");
    setValue("acknowledgedSignature", "", { shouldValidate: true, shouldDirty: true });
    setValue("acknowledgedUploaded", false, { shouldValidate: true });
  };

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
  } = useFieldArray({ control, name: "locations" });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        setJobOrder(data || {});
        setSatisfactionRating(data?.satisfaction_rating || 0);
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client);

        const technician_signed = data?.technicians.map(({ firstname, lastname }) => `${firstname} ${lastname}`);

        setjobOrderParameters((prev) => ({
          ...prev,
          status: data?.status || "",
          inspection_report:  data?.inspection_report || [],
          job_date: data?.job_date || "",
          start_time: data?.start_time || "",
          accomplished_by_technician: technician_signed || []
        }));

        setValue("status", data?.status || "", { shouldValidate: true });
        setEditedClient(data.client);
      } catch (error) {
        setErrorMessage("Error fetching job order:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchJobOrder();
  }, [id]);


  // for edit tech dialog
  useEffect(() => {
    // fetch once
    getTechnicians().then(data => setAllTechnicians(data || [])).catch(console.error);
  }, []);

  const openEditTechs = () => {
    const ids = (jobOrder?.technicians || []).map(t => t.id);
    setEditSelectedTechIds(ids);
    setEditTechsOpen(true);
  };

  const closeEditTechs = () => setEditTechsOpen(false);

  const toggleEditTech = (techId) => (e) => {
    const checked = e.target.checked;
    setEditSelectedTechIds(prev => checked ? [...prev, techId] : prev.filter(id => id !== techId));
  };

  const saveEditTechs = async () => {
    const selected = (allTechnicians || [])
      .filter(t => editSelectedTechIds.includes(t.id))
      .map(({ id, firstname, lastname }) => ({ id, firstname, lastname }));

    try {
      await putJobOrder(jobOrder.id, { technicians: selected });
      // update local UI immediately
      setJobOrder(prev => ({ ...prev, technicians: selected }));

      // keep the names array in sync for your PDF payload
      setjobOrderParameters(prev => ({
        ...prev,
        accomplished_by_technician: selected.map(t => `${t.firstname} ${t.lastname}`)
      }));

      // ensure signatory stays valid + keep RHF in sync
      const validNames = new Set(selected.map(t => `${t.firstname} ${t.lastname}`));
      const nextSignatory =
        technicianSignatory && validNames.has(technicianSignatory)
          ? technicianSignatory
          : "";

      setTechnicianSignatory(nextSignatory);
      setValue("technician_signatory", nextSignatory, { shouldValidate: true });

      setSuccessMessage("Technicians updated.");
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to update technicians.");
    } finally {
      setEditTechsOpen(false);
    }
  };

  
  // Add new states for holding the signature data URLs
  const accomplishedBySignatureRef = useRef(null);
  const acknowledgedBySignatureRef = useRef(null);
  // Declare state for each signature URL
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");

  const [acknowledgedClientSignatory, setAcknowledgedClientSignatory] = useState("");

  // Track when client signature was uploaded (to catch technician delays)
  const [clientSignatureUploadedAt, setClientSignatureUploadedAt] = useState(null);
  // Track when technician signature was uploaded
  const [technicianSignatureUploadedAt, setTechnicianSignatureUploadedAt] = useState(null);

  // Function to recursively convert true/false to True/False in an object
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False"; // Convert true/false to 'True'/'False'
    }
    return obj;
  };

  const handleJObOrderChange = (field, value) => {
    setjobOrderParameters(prev => ({ ...prev, [field]: value }));
    if (field === "status") {
      setValue("status", value, { shouldValidate: true, shouldDirty: true });
      if (value !== "Not Done") clearErrors("status");
    }
  };

  // 1. derive a lookup from the raw arrays for label ↔ value mapping
  const formatKey = s => s.toLowerCase().replace(/\s+/g, "_");

  // 2. filter & sort selected job types
  const selectedJobTypes = (jobOrder?.job_types || [])
    // map back to the human-readable label
    .map(val => JOB_TYPES.find(opt => formatKey(opt) === val))
    // remove any non-matches
    .filter(Boolean)
    // alphabetical
    .sort((a, b) => a.localeCompare(b));

  // 3. same for descriptions
  const selectedJobDescriptions = (jobOrder?.job_description || [])
    .map(val => JOB_DESCRIPTIONS.find(opt => formatKey(opt) === val))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const [chemical_used, setChemicalUsed] = useState([
    { label: "1", chemical: "", dilution: "" },
    { label: "2", chemical: "", dilution: "" },
    { label: "3", chemical: "", dilution: "" },
    { label: "4", chemical: "", dilution: "" },
    { label: "5", chemical: "", dilution: "" },
    { label: "6", chemical: "", dilution: "" },
  ]);

  const handleChemicalInputChange = (index, field, value) => {
    const newChemicalUsed = [...chemical_used];
    newChemicalUsed[index][field] = value;
    setChemicalUsed(newChemicalUsed);
  };

  const [equipment_used, setEquipmentUsed] = useState([
    { label: "Hand Sprayer", usage: false },
    { label: "Power Sprayer", usage: false }
  ]);

  const handleEquipmentCheckboxChange = (index) => {
    const newEquipmentUsed = [...equipment_used];
    newEquipmentUsed[index].usage = !newEquipmentUsed[index].usage;
    setEquipmentUsed(newEquipmentUsed);
  };

  const [service_report_payment_status, setServiceReportPaymentStatus] =
    useState([
      { label: "Paid", selection: false },
      { label: "COD", selection: false },
      { label: "For Collection", selection: false },
    ]);

  const [service_report_payment_method, setServiceReportPaymentMethod] = useState([
    { label: "Cash", selection: false },
    { label: "Check", selection: false },
    { label: "Free", selection: false },
  ]);

  // Handler for payment status
  const handlePaymentStatusChange = (statusIndex) => {
    const newStatusOptions = service_report_payment_status.map(
      (status, index) => ({
        ...status,
        selection: index === statusIndex ? !status.selection : false,
      })
    );
    setServiceReportPaymentStatus(newStatusOptions);
  };

  // Handler for payment method
  const handlePaymentMethodChange = (methodIndex) => {
    const newMethodOptions = service_report_payment_method.map((method, index) => ({
      ...method,
      selection: index === methodIndex ? !method.selection : false,
    }));
    setServiceReportPaymentMethod(newMethodOptions);
  };

  // Location Type (checkboxes)
  const [structureType, setStructureType] = useState({
    residential: false,
    commercial: false,
    industrial: false,
  });

  const handleStructureTypeChange = (field) => (event) => {
    setStructureType((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  // Structure Details (text fields)
  const [structureDetails, setStructureDetails] = useState({
    bedrooms: "",
    restrooms: "",
    floors: "",
  });

  const handleStructureDetailsChange = (field) => (e) => {
    const v = e.target.value;
    if (v === "" || /^[0-9]+$/.test(v)) {
      setStructureDetails(prev => ({ ...prev, [field]: v }));
    }
  };

  // Termite Activity (mutually exclusive checkboxes)
  const [termiteActivity, setTermiteActivity] = useState(""); // "", "with", "without"
  const handleTermiteActivity = (value) => () => {
    setTermiteActivity(prev => (prev === value ? "" : value));
  };

  const resizeAndUploadSignature = async (signatureRef, jobOrderId, type) => {
    // 1. grab the trimmed canvas
    if (!signatureRef.current) throw new Error("No signature canvas available");
    const canvas = signatureRef.current.getTrimmedCanvas();
    if (!canvas) throw new Error("Nothing to upload");

    // 2. draw it into a fixed‐size canvas
    const size = 200;
    const c2 = document.createElement("canvas");
    c2.width = size;
    c2.height = size;
    c2.getContext("2d").drawImage(canvas, 0, 0, size, size);

    // 3. turn into a blob, rejecting if null
    const blob = await new Promise((resolve, reject) => {
      c2.toBlob(b => (b ? resolve(b) : reject(new Error("Canvas toBlob returned null"))), "image/png");
    });

    // 4. upload
    const key = `${jobOrderId}-signature-${type}.png`;
    const params = {
      Bucket: TEMP_BUCKET_NAME,
      Key: key,
      Body: blob,
      ContentType: "image/png",
    };
    console.log("Uploading signature:", params);

    const resp = await s3TempBucketUploader.upload(params).promise();
    // AWS SDK v2 returns resp.Location; if not, you can fall back to the public URL:
    return resp.Location || `https://${params.Bucket}.s3.amazonaws.com/${params.Key}`;
  };

  const handleGeneratePDF = async (type) => {
    // Merge contact details with jobOrderParameters
    const pdfData = {
      ...jobOrderParameters,
      accomplishedSignature: accomplishedSignatureURL,
      acknowledgedSignature: acknowledgedSignatureURL,
    };

    console.log("Job Order Parameters:", pdfData);

    try {
      setIsLoading(true);
      const pdfBlob =
        type === "invoice"
          ? await generateReceipts(pdfData)
          : await generateReports(pdfData);
      if (!pdfBlob) {
        throw new Error("Failed to generate PDF blob.");
      }
      const s3Url = await uploadPDF(pdfBlob, jobOrder.id, type);
      console.log(`${type} PDF uploaded to S3 and DynamoDB updated:`, s3Url);

      // Prepare update data with the correct link field
      const updateData = {
        [`${type === "invoice" ? "receipts_url" : "reports_url"}`]: s3Url,
      };

      // Update DynamoDB with the S3 URL
      await patchJobOrderLink(jobOrder.id, updateData);
      console.log(`DynamoDB updated with ${type} URL:`, s3Url);
    } catch (error) {
      console.error("Error generating or uploading PDF:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadPDF = async (pdfBlob, jobOrderId, type) => {
    console.log("Generated PDF Blob:", pdfBlob);

    const fileName = `${jobOrderId}-${type}.pdf`;
    const s3Uploader =
      type === "invoice" ? s3InvoicePDFUploader : s3ReportsPDFUploader;
    const params = {
      Bucket: type === "invoice" ? INVOICE_BUCKET_NAME : REPORTS_BUCKET_NAME,
      Key: `${fileName}`,
      Body: pdfBlob,
      ContentType: "application/pdf",
    };

    console.log("S3 Upload Parameters:", params); // Debugging line to view upload parameters

    try {
      await s3Uploader.upload(params).promise();
      return `https://${params.Bucket}.s3.amazonaws.com/${fileName}`;
    } catch (error) {
      console.error("S3 upload error:", error);
      throw new Error("Upload failed");
    }
  }; 

  // Function to format date
  // TODO: you can use date-fns for more advanced date formatting
  function formatDate(dateString) {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date format");
      }

      return new Intl.DateTimeFormat("en-US", options).format(date);
    } catch (error) {
      console.error(error.message);
      return "Invalid Date"; // Fallback for invalid dates
    }
  }

  const formattedDate = formatDate(jobOrderParameters.job_date);

  const handleSendEmail = async () => {
    // const generateAttachmentLinks = (links, hasTermiteControl) => {
    const generateAttachmentLinks = (links, False) => {
      // Determine which keys to use for the links
      const baseUrl = `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}`;
      return {
        // certificate_url: `${baseUrl}/${jobOrder.id}_certificate.pdf`,
        receipts_url: `${baseUrl}/${jobOrder.id}_final_receipt.pdf`
      };
    };

    // Generate the attachment links
    const attachmentLinks = generateAttachmentLinks();

    // Create the HTML list items from the links
    const attachmentLinksHTML = Object.entries(attachmentLinks)
      .map(([key, url]) => {
        const fileName = key
          .replace(/_url$/, "")
          .replace(/_/g, " ")
          .toUpperCase(); // Remove '_url' and format
        return `<li><a href="${url}" target="_blank">${fileName}</a></li>`;
      })
      .join("");

    const htmlBody = `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          .email-container {
            width: 100%;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            
            border: 1px solid #ddd;
          }
          .header, .footer {
            width: 100%;
            text-align: center;
            margin: 0;
            padding: 20px 0;
            
          }
          .content {
            margin: 20px 0;
          }
           .content img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0 auto;
          }
        </style>
      </head>
      <body>
        <div class="email-container">
          <!-- Header Section -->
          <div class="header">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/header_boaz_transparent1.png" alt="Boaz Header" style="max-width: 100%; height: auto;">
          </div>

          <!-- Main Content -->
          <div class="content">
            <body>            
            <p>Sir/Ma'am,</p>
            <p></p>
            <p>Good day!</p>
            <p></p>
            <p>We are pleased to send you our Accomplishment Report for the visit done on  ${formattedDate}.</p>
            <p>We thank you and please let us know if have any questions.</p>
            <p></p>            
            <p>Sincerely,</p>
            <p>Boaz Pest Control - Accounts Team</p>
            <ul>
              ${attachmentLinksHTML}
            </ul>
            <hr />                     
          </body>            
          </div>

          <!-- Footer Section -->
          <a href="mailto:boazpestcontrol@gmail.com" style="text-decoration: none;">
          <div class="footer">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/footer_boaz_transparent1.png" alt="Boaz Footer" style="max-width: 100%; height: auto;">
            <hr />
            <footer>              
              <p>If you no longer wish to receive these emails, you can <a href="mailto:unsubscribe-boaz@maestrosystemsinc.com">unsubscribe here</a>.</p>
            </footer>
          </div>
          </a>
        </div>
      </body>
    </html>`;

    const lambdaPayload = {
      // from: "boaz-billing@maestrosystemsinc.com",
      from: "boaz-reminders@maestrosystemsinc.com",
      recipients: [jobOrder?.client?.contact?.email?.email],
      subject: `Service Report for Job Order ${jobOrder?.id}`,
      body: htmlBody,
    };

    try {
      // Only send email if recipient email exists
      if (jobOrder?.client?.contact?.email?.email) {
        await sendEmailQueue(lambdaPayload);
        console.log("✅ Email notification sent successfully");
      } else {
        console.warn("⚠️ Email notification skipped - no client email address");
      }
    } catch (error) {
      // Log but don't show error to user - email is optional
      console.warn("⚠️ Email notification failed (non-critical):", error.message);
    }
  };

  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  // Determine numeric dimensions based on breakpoints
  let canvasWidth, canvasHeight;
  if (isLg) {
    canvasWidth = 300;
    canvasHeight = 350;
  } else if (isMd) {
    canvasWidth = 250;
    canvasHeight = 325;
  } else {
    canvasWidth = 200;
    canvasHeight = 300;
  }

  let ackWidth, ackHeight;
  if (isLg) {
    ackWidth = 300;
    ackHeight = 150;
  } else if (isMd) {
    ackWidth = 250;
    ackHeight = 125;
  } else {
    ackWidth = 200;
    ackHeight = 100;
  }

  const onSubmit = async (formData) => {
    setIsLoading(true);

    const locations = formData?.locations || [];

    // Build at least 19 rows
    const inspection_report = Array.from({ length: Math.max(19, locations.length) }).map((_, idx) => {
      const loc = locations[idx] || {
        location: "",
        location_other: "",
        findings: "",
        action_taken: "",
        bait_monitor_location: "",
        remarks: "",
      };

      const location =
        loc.location === "Other" ? (loc.location_other || "") : (loc.location || "");
      const findings = (loc.findings || "").trim();

      // human-readable action taken
      const action_taken =
        loc.action_taken === "baiting"
          ? `Baiting System Installation/ AG Monitoring in ${loc.bait_monitor_location || ""}`.trim()
          : ({ soil: "Soil Treatment", spraying: "Spraying", monitoring: "Monitoring only" }[loc.action_taken] || "");

      const remarks = (loc.remarks || "").trim();

      return { location, findings, action_taken, remarks };
    });

    const updatedJobOrderParameters = {
      ...jobOrderParameters,
      inspection_report,
      chemical_used,
      equipment_used,
      service_report_payment_status,
      service_report_payment_method,
      accomplishedSignature: formData.accomplishedSignature || accomplishedSignatureURL,
      acknowledgedSignature: formData.acknowledgedSignature || acknowledgedSignatureURL,
      acknowledgedClientSignatory: acknowledgedClientSignatory,
      acknowledgedClientSignatory: formData.acknowledgedClientSignatory,
      technician_signatory: formData.technician_signatory,
      satisfaction_rating: formData.satisfaction_rating ?? jobOrderParameters.satisfaction_rating,
      structure_type: {
        residential: structureType.residential,
        commercial: structureType.commercial,
        industrial: structureType.industrial,
      },
      structure_details: {
        bedrooms: structureDetails.bedrooms,
        restrooms: structureDetails.restrooms,
        floors: structureDetails.floors,
      },
      service_report_status: "Done",
      technician_signatory: technicianSignatory,
      termite_activity: termiteActivity || "",
      termite_recommendations: formData.termite_recommendations || [],
      technicians: jobOrder?.technicians || [],
      // ⏰ TIMESTAMP TRACKING (prevents technician time-cheating)
      technician_signature_uploaded_at: technicianSignatureUploadedAt,  // When technician signs
      client_signature_uploaded_at: clientSignatureUploadedAt,  // When UPLOAD was clicked
      report_submitted_at: new Date().toISOString(),  // When SUBMIT was clicked
    };

    // Exterra (cap at 8 just in case)
    const { exterra_bait_stations = [] } = formData;
    updatedJobOrderParameters.exterra_bait_stations = exterra_bait_stations.slice(0, 8);

    const { id, ...putClientParameters } = editedClient;

    //Added for Document Generation Receipts, Reports, Certificate
    try {
      // 1) transform & convert booleans
      const payload = convertBooleans({ jobOrder, updatedJobOrderParameters, form: formData });
      // 2) call your PDF “field-mapper”
      const response = await submitDocumentFieldMapper(payload);
      console.log("Job order parameters submitted successfully:", response);

      setLinks(
        {
          // reports_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_report.pdf`,
          // certificate_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_certificate.pdf`,
          receipts_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_final_receipt.pdf`,
        }
      );

      // Send the email after updating links

      setSuccessMessage(
        "Service Report and documents and generated successfully! Redirecting..."
      );

      setIsLoading(false);
      await handleSendEmail();

      // 💾 SAVE ALL UPDATED PARAMETERS TO DATABASE (including timestamps)
      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      await putClient(id, putClientParameters);
      // All parameters saved successfully

      // Redirect to service reports list after 2 seconds
      setTimeout(() => {
        navigate("/manage-service-reports");
      }, 2000);
    } catch (error) {
      console.error("Error submitting job order parameters:", error);
      setErrorMessage(
        `Failed to generate Service Report and Documents: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false); // Set loading to false
    }
  }

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Create Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="job-order-form">
        <Grid container>
          {/* Basic Information Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginTop: { xs: "-5px", md: "5px", lg: "10px", xl: "20px" },
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Basic Information
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {" "}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} lg={12} xl={2}>
                    <TextField
                      name="id"
                      label="Job Order ID"
                      value={jobOrder?.id || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={2}>
                    <FormControl fullWidth size="small" error={!!errors.status}>
                      <InputLabel id="job-order-status-label">
                        Job Order Status
                      </InputLabel>
                      <Select
                        labelId="job-order-status-label"
                        name="status"
                        value={jobOrderParameters?.status || ""}
                        label="Job Order Status"
                        onChange={(e) =>
                          handleJObOrderChange("status", e.target.value)
                        }
                      >
                        <MenuItem value="Not Done">Not Done</MenuItem>
                        <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                        <MenuItem value="Done">Done</MenuItem>
                        <MenuItem value="Cancelled By Client">
                          Cancelled By Client
                        </MenuItem>
                      </Select>
                      {errors.status && <FormHelperText>{errors.status.message}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client ID"
                      label="Client ID"
                      value={jobOrder?.client?.id || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client Name"
                      label="Client Name"
                      value={jobOrder?.client?.name || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12} lg={12} xl={10}>
                        <TextField
                          label="Job Site Address"
                          variant="outlined"
                          fullWidth
                          value={
                            jobOrder?.client?.address
                              ? `${jobOrder?.client?.address.house_unit_street || ""
                              }, ${jobOrder?.client?.address.barangay || ""
                              }, ${jobOrder?.client?.address.city || ""
                              }, ${jobOrder?.client?.address.metro_province || ""} ${jobOrder?.client?.address.zipcode || ""
                              }`
                              : ""
                          }
                          disabled
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={12} xl={2}>
                        <TextField
                          name="area"
                          label="Area"
                          value={jobOrder?.client?.address?.area || ""}
                          fullWidth
                          disabled
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      variant="outlined"
                      value={
                        jobOrder?.client?.contact
                          ? `${jobOrder?.client?.contact.lastname || ""}, ${jobOrder?.client?.contact.firstname || ""
                          }`
                          : ""
                      }
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      variant="outlined"
                      value={jobOrder?.client?.contact?.email?.email || ""}
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      variant="outlined"
                      value={
                        jobOrder?.client?.contact?.number
                          ? `${jobOrder?.client?.contact.number.type}: ${jobOrder?.client?.contact.number.number}`
                          : ""
                      }
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      label="Location Type"
                      value={
                        client?.location_type
                          ? client.location_type
                          : ""
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ fontSize: "0.875rem" }}
                      disabled
                    />
                  </Grid>           
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Type Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle2">Job Type</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {selectedJobTypes.map(option => (
                    <Box
                      component="li"
                      key={option}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        mb: 0.5,
                      }}
                    >
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Description Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle2">Job Description</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {selectedJobDescriptions.map(option => (
                    <Box
                      component="li"
                      key={option}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        mb: 0.5,
                      }}
                    >
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Special Notes Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Special Notes
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Special Notes"
                      name="special_notes"
                      type="text"
                      value={jobOrder?.special_notes || ""}
                      fullWidth
                      disabled
                      multiline
                      rows={4}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Appointment Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Appointment
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {/* Appointment Type */}
                  <Grid item xs={12} md={6} lg={6} xl={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="appointment-type-label">
                        Appointment Type
                      </InputLabel>
                      <Select
                        labelId="appointment-type-label"
                        value={jobOrder?.appointment_type || ""}
                        label="Appointment Type"
                        disabled
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="Initial">Initial</MenuItem>
                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {/* Treatment Type */}
                  <Grid item xs={12} md={6} lg={6} xl={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="treatment-type-label">
                        Treatment Type
                      </InputLabel>
                      <Select
                        labelId="treatment-type-label"
                        value={jobOrder?.treatment_type || ""}
                        label="Treatment Type"
                        disabled
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="QB">QB</MenuItem>
                        <MenuItem value="FFUP">FFUP</MenuItem>
                        <MenuItem value="LT">LT</MenuItem>
                        <MenuItem value="QBLT">QBLT</MenuItem>
                        <MenuItem value="Backjob">Backjob</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Structure Type Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                  >
                    Structure Type
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <FormGroup sx={{ width: "100%" }}>
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={structureType.residential}
                        onChange={handleStructureTypeChange("residential")}
                      />
                    }
                    label="Residential"
                  />
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={structureType.commercial}
                        onChange={handleStructureTypeChange("commercial")}
                      />
                    }
                    label="Commercial"
                  />
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={structureType.industrial}
                        onChange={handleStructureTypeChange("industrial")}
                      />
                    }
                    label="Industrial"
                  />
                </FormGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* Structure Details Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Structure Details
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Number of Bedrooms"
                      type="number"
                      value={structureDetails.bedrooms}
                      onChange={handleStructureDetailsChange("bedrooms")}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Number of Restrooms"
                      type="number"
                      value={structureDetails.restrooms}
                      onChange={handleStructureDetailsChange("restrooms")}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Number of Floors"
                      type="number"
                      value={structureDetails.floors}
                      onChange={handleStructureDetailsChange("floors")}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Termite Activity */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Termite Activity
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <FormGroup sx={{ width: "100%" }}>
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={termiteActivity === "with"}
                        onChange={handleTermiteActivity("with")}
                      />
                    }
                    label="With Active Termites"
                  />
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={termiteActivity === "without"}
                        onChange={handleTermiteActivity("without")}
                      />
                    }
                    label="Without Active Termites"
                  />
                </FormGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* Inspection Report (Mobile) */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Inspection Report
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <Stack spacing={2}>
                  {locationFields.map((loc, idx) => (
                    <TermiteLocationCard
                      key={loc.id}
                      control={control}
                      register={register}
                      index={idx}
                      removeLocation={removeLocation}
                    />
                  ))}

                  <Button
                    variant="contained"
                    disabled={locationFields.length >= 19}
                    fullWidth
                    onClick={() =>
                      appendLocation({
                        location: "",
                        location_other: "",
                        findings: "",
                        action_taken: "",
                        bait_monitor_location: "",
                        remarks: "",
                      })
                    }
                  >
                    Add New Location ({locationFields.length}/19)
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Recommendations Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2, mt: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Recommendations
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <FormGroup sx={{ width: "100%" }}>
                  {RECOMMENDATIONS.map((label) => (
                    <Controller
                      key={label}
                      name="termite_recommendations"
                      control={control}
                      render={({ field: { value = [], onChange } }) => {
                        const checked = value.includes(label);
                        return (
                          <FormControlLabel
                            sx={{ width: "100%" }}
                            control={
                              <Checkbox
                                checked={checked}
                                onChange={(e) =>
                                  onChange(
                                    e.target.checked
                                      ? [...value, label]
                                      : value.filter((v) => v !== label)
                                  )
                                }
                              />
                            }
                            label={label}
                          />
                        );
                      }}
                    />
                  ))}
                </FormGroup>
              </CardContent>
              {errors?.termite_recommendations && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {errors.termite_recommendations.message}
                </FormHelperText>
              )}
            </Card>
          </Grid>

          {/* Exterra Section */}
          <Grid item xs={12}>
            <ExterraCard control={control} register={register} />
          </Grid>

          {/* Chemical Used Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            Chemical Used / Batch # / Expiration
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ fontWeight: "bold" }}
                          >
                            Dilution
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            Equipment Used
                          </TableCell>                          
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chemical_used.map((chemical, index) => (
                          <TableRow key={index}>
                            <TableCell>{chemical.label}:</TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chemical.chemical}
                                onChange={(e) =>
                                  handleChemicalInputChange(
                                    index,
                                    "chemical",
                                    e.target.value
                                  )
                                }
                                sx={{ minWidth: 100 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chemical.dilution}
                                onChange={(e) =>
                                  handleChemicalInputChange(
                                    index,
                                    "dilution",
                                    e.target.value
                                  )
                                }
                                sx={{ minWidth: 50 }}
                              />
                            </TableCell>
                            {index < equipment_used.length ? (
                              <>
                                <TableCell>
                                  {equipment_used[index].label}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={equipment_used[index].usage}
                                    onChange={() =>
                                      handleEquipmentCheckboxChange(index)
                                    }
                                  />
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                              </>
                            )}                            
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Reminder Notes */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    Reminder Notes
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <TextField
                  label="Reminder Notes"
                  value={jobOrderParameters?.reminder_notes || ""}
                  onChange={e =>
                    handleJObOrderChange("reminder_notes", e.target.value)
                  }
                  fullWidth
                  multiline
                  rows={6}
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Service Fee */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    Service Fee
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <TextField
                  label="Service Fee"
                  value={jobOrderParameters?.service_fee || ""}
                  onChange={e =>
                    handleJObOrderChange("service_fee", e.target.value)
                  }
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₱</InputAdornment>
                  }}
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Status */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={<Typography variant="h6">Payment Status</Typography>}
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
                  <Table size="small">
                    <TableBody>
                      {(service_report_payment_status || []).map((statusOpt, idx) => {
                        const methodOpt = (service_report_payment_method || [])[idx] || {};
                        return (
                          <TableRow key={statusOpt.label} hover>
                            <TableCell>{statusOpt.label}</TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={statusOpt.selection}
                                onChange={() => handlePaymentStatusChange(idx)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ color: 'text.secondary', p: 0 }}>
                              •
                            </TableCell>
                            <TableCell/>
                            <TableCell>{methodOpt.label || ""}</TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={methodOpt.selection}
                                onChange={() => handlePaymentMethodChange(idx)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Accomplished By and Acknowledged By Signature Section */}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Grid container spacing={3}>
                    {/* Service Accomplished by Section */}
                    <Grid item xs={12} md={12}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Service Accomplished by:
                          </Typography>
                          <Button size="small" variant="outlined" onClick={openEditTechs} sx={{ mb: 2 }}>
                            Edit technicians
                          </Button>
                        </Grid>
                      </Grid>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Grid container spacing={1}>
                            {jobOrder?.technicians?.length > 0 ? (
                              jobOrder?.technicians.map((technician, index) => (
                                <Grid
                                  item
                                  xs={12}
                                  md={12}
                                  lg={12}
                                  xl={12}
                                  key={technician.id}
                                  mb={2}
                                >
                                  <TextField
                                    label={`Technician ${index + 1}`}
                                    variant="outlined"
                                    fullWidth
                                    value={`${technician.firstname} ${technician.lastname}`}
                                    disabled
                                    size="small"
                                  />
                                </Grid>
                              ))
                            ) : (
                              <Grid item xs={12} md={12} lg={12} xl={12}>
                                <TextField
                                  label="Technician"
                                  variant="outlined"
                                  fullWidth
                                  value="No Technicians Available"
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  size="small"
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                        <Grid item xs={12} md={6}>                          
                          <Box
                            sx={{
                              width: canvasWidth,
                              height: ackHeight,
                              border: "2px solid #000",
                              padding: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <SignatureCanvas
                              ref={accomplishedBySignatureRef}
                              penColor="black"
                              canvasProps={{
                                width: canvasWidth,
                                height: ackHeight,
                                className: "sigCanvas",
                              }}
                            />
                          </Box>

                        </Grid>
                      </Grid>

                      {/* Technician Signatory Dropdown */}
                      <Grid item xs={12}>
                        <FormControl fullWidth margin="normal" error={!!errors.technician_signatory}>
                          <InputLabel id="technician-signatory-label">
                            Technician Signatory
                          </InputLabel>
                          <Select
                            labelId="technician-signatory-label"
                            id="accomplished-by-technician"
                            value={technicianSignatory}
                            label="Technician Signatory"
                            onChange={(e) => {
                            const val = e.target.value;
                              setTechnicianSignatory(val);                           // keep local UI state
                              setValue("technician_signatory", val, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });                                                    // tell RHF/Zod
                              clearErrors("technician_signatory");
                            }}
                          >
                            {jobOrder?.technicians?.map((tech) => (
                              <MenuItem
                                key={tech.id}
                                value={`${tech.firstname} ${tech.lastname}`}
                              >
                                {`${tech.firstname} ${tech.lastname}`}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.technician_signatory && (
                            <FormHelperText error>{errors.technician_signatory.message}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <input type="hidden" {...register("technician_signatory")} />

                      {/* Action Buttons for Accomplished Signature */}
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        mt={2}
                        mb={2}
                        mr={2}
                      >
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={clearAccomplishedSignature}
                          sx={{
                            fontSize: "0.875rem",
                            color: "#FF4C4C",
                            borderColor: "#FF4C4C",
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveAndUploadAccomplishedSignature}
                          sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
                        >
                          Upload
                        </Button>
                      </Grid>
                      {errors?.accomplishedUploaded && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors.accomplishedUploaded.message}
                        </FormHelperText>
                      )}

                      {/* ⏰ TIMESTAMP DISPLAY - Technician Signature (HIDDEN FROM TECHNICIANS) */}
                      {!isTechnician && technicianSignatureUploadedAt && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                          <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                            🔵 Technician Signature Uploaded At:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#0d47a1', mt: 0.5 }}>
                            {new Date(technicianSignatureUploadedAt).toLocaleString('en-US', {
                              month: 'short',
                              day: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: true
                            })}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#1976d2', mt: 0.5, display: 'block' }}>
                            This timestamp is saved and cannot be changed.
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Schedule Section */}
            <Grid
              item
              xs={12}
              md={12}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                      sx={{                      
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        fontSize: "1rem",
                      }}
                    >
                      Schedule
                    </Typography>
                  }
                />
                <CardContent sx={{ flex: 1 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Start Date"
                        name="job_date"
                        type="date"
                        value={jobOrderParameters.job_date}
                        onChange={(e) =>
                          handleJObOrderChange("job_date", e.target.value)
                        }
                        fullWidth
                        size="small"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Start Time"
                        name="start_time"
                        type="time"
                        value={jobOrderParameters.start_time}
                        onChange={(e) =>
                          handleJObOrderChange("start_time", e.target.value)
                        }
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={6}>
                      <TextField
                        label="End Date"
                        name="end_date"
                        type="date"
                        value={jobOrderParameters.end_date}
                        onChange={(e) =>
                          handleJObOrderChange("end_date", e.target.value)
                        }
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: {
                            min: jobOrderParameters.job_date || undefined
                          }
                        }}
                      />
                    </Grid> */}

                    {/* <Grid item xs={12} md={4}>
                      <TextField
                        label="End Time"
                        name="end_time"
                        type="time"
                        value={jobOrderParameters.end_time}
                        onChange={(e) =>
                          handleJObOrderChange("end_time", e.target.value)
                        }
                        fullWidth
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                size="small"
                                onClick={() => handleJObOrderChange("end_time", getNowHHMM())}
                              >
                                Now
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid> */}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* For Client Only Section */}
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardHeader
                  title={
                    <Typography variant="subtitle2"
                                sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                      For Client Only
                    </Typography>
                  }
                  sx={{ px: 2, pt: 2, pb: 0 }}
                />

                <CardContent sx={{ px: 2, pb: 2 }}>
                  {/* Satisfaction label */}
                  <Typography variant="body2">
                    Your Satisfaction Rating:                    
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    (1 – lowest, 5 – highest)
                  </Typography>

                  {/* Rating buttons */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    {[1, 2, 3, 4, 5].map((n) => {
                      const selected = satisfactionRating === n;
                      return (
                        <Button
                          key={n}
                          onClick={() => {
                            setSatisfactionRating(n);
                            setjobOrderParameters(prev => ({
                              ...prev,
                              satisfaction_rating: n
                            }));
                            // ✅ inform RHF/Zod that a real rating exists
                            setValue("satisfaction_rating", n, { shouldValidate: true, shouldDirty: true });
                          }}
                          sx={{
                            fontSize: '1.25rem',
                            minWidth: '40px',
                            height: '40px',
                            p: 0,
                            borderRadius: '50%',
                            transition: 'all 0.2s ease-in-out',
                            // selected styles
                            bgcolor: selected ? 'primary.main' : 'transparent',
                            color: selected ? 'common.white' : 'text.secondary',
                            border: selected ? 'none' : '1px solid',
                            borderColor: selected ? 'transparent' : 'divider',
                            transform: selected ? 'scale(1.2)' : 'scale(1)',
                            '&:hover': {
                              bgcolor: selected ? 'primary.dark' : 'grey.100',
                            },
                            cursor: 'pointer'
                          }}
                        >
                          {n}
                        </Button>
                      );
                    })}
                  </Box>
                  {errors?.satisfaction_rating && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {errors.satisfaction_rating.message}
                    </FormHelperText>
                  )}
               
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Acknowledged By:
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>                        
                        <Box
                          sx={{
                            width: ackWidth,
                            height: ackHeight,
                            border: "2px solid #000",
                            padding: 1,
                            marginTop: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <SignatureCanvas
                            ref={acknowledgedBySignatureRef}
                            penColor="black"
                            canvasProps={{
                              width: ackWidth,
                              height: ackHeight,
                              className: "sigCanvas",
                            }}
                          />
                        </Box>

                      </Grid>
                    </Grid>
                    <Grid container spacing={3} sx={{ marginTop: 2 }}>
                      <Grid item xs={12} md={12} lg={12}>                        
                        <TextField
                          fullWidth
                          label="Client Representative"
                          name="Client Representative"
                          variant="outlined"
                          value={acknowledgedClientSignatory}
                          onChange={(e) => {
                            const val = e.target.value;
                            setAcknowledgedClientSignatory(val);                    // keep local UI state
                            setValue("acknowledgedClientSignatory", val, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });                                                     // RHF/Zod
                            clearErrors("acknowledgedClientSignatory");
                          }}
                          size="small"
                        />
                        {errors.acknowledgedClientSignatory && (
                          <FormHelperText error sx={{ mt: 1 }}>
                            {errors.acknowledgedClientSignatory.message}
                          </FormHelperText>
                        )}
                      </Grid>
                    </Grid>
                    <input type="hidden" {...register("acknowledgedClientSignatory")} />

                    {/* Action Buttons for Acknowledged Signature */}
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="center"
                      mt={2}
                      mb={2}
                      mr={2}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={clearAcknowledgedSignature}
                        sx={{
                          fontSize: "0.875rem",
                          color: "#FF4C4C",
                          borderColor: "#FF4C4C",
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveAndUploadAcknowledgedSignature}
                        sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
                      >
                        Upload
                      </Button>
                    </Grid>
                    {errors?.acknowledgedUploaded && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {errors.acknowledgedUploaded.message}
                      </FormHelperText>
                    )}

                    {/* ⏰ TIMESTAMP DISPLAY - Shows when signature was uploaded (HIDDEN FROM TECHNICIANS) */}
                    {!isTechnician && clientSignatureUploadedAt && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                          ✅ Client Signature Uploaded At:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.5 }}>
                          {new Date(clientSignatureUploadedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                          })}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#558b2f', mt: 0.5, display: 'block' }}>
                          This timestamp is saved and cannot be changed.
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </CardContent>

              </Card>
            </Grid>
          </Grid>
          
          {/* Dialog for Edit Technicians */}
          <Dialog open={editTechsOpen} onClose={closeEditTechs} maxWidth="xs" fullWidth>
            <DialogTitle>Edit Technicians</DialogTitle>
            <DialogContent dividers>
              <Table size="small">
                <TableBody>
                  {allTechnicians.map(t => {
                    const notes = [];
                    if (t.competency_level?.driving_4_wheels && t.competency_level.driving_4_wheels !== "N/A") {
                      notes.push("Driver: 4 wheels");
                    }
                    if (t.competency_level?.driving_2_wheels && t.competency_level.driving_2_wheels !== "N/A") {
                      notes.push("Driver: 2 wheels");
                    }
                    const checked = editSelectedTechIds.includes(t.id);
                    return (
                      <TableRow key={t.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox checked={checked} onChange={toggleEditTech(t.id)} />
                        </TableCell>
                        <TableCell>{`${t.lastname}, ${t.firstname}`}</TableCell>
                        <TableCell>{notes.join(", ")}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeEditTechs}>Cancel</Button>
              <Button variant="contained" disabled={!editSelectedTechIds.length} onClick={saveEditTechs}>
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Display Receipts and Reports Links */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Document Links
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {links?.receipts_url ? (
                    <>
                      {links?.receipts_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={links.receipts_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Receipts.pdf{" "}
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body1" color="textSecondary">
                        No document links available.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Form buttons */}
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/manage-service-reports")}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              disabled={isLoading}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default TermiteControlForm;
